import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * LR Direct ATLAS Broker Match — real broker-performance data only.
 * Backed by the security-definer RPC get_network_broker_performance, which
 * scopes strictly to networks owned by the calling shipper.
 * No composite score is ever produced below SCORE_MIN_BIDS evidence.
 */

/** Bids required before a broker gets a composite match score. */
export const SCORE_MIN_BIDS = 5;
/** Bids required before we call the history "limited" rather than absent. */
export const LIMITED_MIN_BIDS = 3;

export interface BrokerPerfRow {
  broker_user_id: string;
  broker_company_id: string | null;
  broker_name: string;
  network_ids: string[];
  bids: number;
  wins: number;
  lanes: number;
  shared_lanes: number;
  median_quote_minutes: number | null;
  last_bid_at: string | null;
}

export type EvidenceTier = "scored" | "limited" | "none";

export interface RankedBroker extends BrokerPerfRow {
  tier: EvidenceTier;
  awardRate: number | null;
  /** 0-100 composite; null unless tier === "scored". */
  score: number | null;
  evidence: string;
}

function plural(n: number, word: string) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

function buildEvidence(r: BrokerPerfRow): string {
  if (r.bids === 0) return "No bids on your loads yet";
  const parts = [plural(r.bids, "bid"), plural(r.wins, "win")];
  if (r.shared_lanes > 0) parts.push(`${plural(r.shared_lanes, "shared lane")}`);
  if (r.median_quote_minutes != null) {
    const m = r.median_quote_minutes;
    parts.push(m < 60 ? `${Math.round(m)} min to quote` : `${(m / 60).toFixed(1)} hrs to quote`);
  }
  return parts.join(" · ");
}

export function rankBrokers(rows: BrokerPerfRow[]): RankedBroker[] {
  const tiered = rows.map((r) => {
    const tier: EvidenceTier =
      r.bids >= SCORE_MIN_BIDS ? "scored" : r.bids >= LIMITED_MIN_BIDS ? "limited" : "none";
    return {
      ...r,
      tier,
      awardRate: r.bids > 0 ? r.wins / r.bids : null,
      score: null as number | null,
      evidence: buildEvidence(r),
    };
  });

  const scored = tiered.filter((r) => r.tier === "scored");
  if (scored.length) {
    // Normalise within the scored cohort only — every input is a real measured value.
    const maxShared = Math.max(...scored.map((r) => r.shared_lanes), 1);
    const speeds = scored.map((r) => r.median_quote_minutes).filter((m): m is number => m != null);
    const fastest = speeds.length ? Math.min(...speeds) : null;
    const slowest = speeds.length ? Math.max(...speeds) : null;

    for (const r of scored) {
      const award = r.awardRate ?? 0;
      const laneFit = r.shared_lanes / maxShared;
      let speedFit = 0.5;
      if (r.median_quote_minutes != null && fastest != null && slowest != null) {
        speedFit = slowest === fastest ? 1 : 1 - (r.median_quote_minutes - fastest) / (slowest - fastest);
      }
      r.score = Math.round((0.45 * award + 0.25 * laneFit + 0.3 * speedFit) * 100);
    }
  }

  const tierRank = { scored: 0, limited: 1, none: 2 } as const;
  return tiered.sort((a, b) => {
    if (tierRank[a.tier] !== tierRank[b.tier]) return tierRank[a.tier] - tierRank[b.tier];
    if (a.tier === "scored") return (b.score ?? 0) - (a.score ?? 0);
    if (b.bids !== a.bids) return b.bids - a.bids;
    return a.broker_name.localeCompare(b.broker_name);
  });
}

export function useLRDirectBrokerMatch(networkId?: string | null) {
  const [rows, setRows] = useState<RankedBroker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await (supabase as any).rpc("get_network_broker_performance", {
      _network_id: networkId ?? null,
    });
    if (rpcError) {
      setError(rpcError.message);
      setRows([]);
    } else {
      setRows(rankBrokers(((data as BrokerPerfRow[]) || []).map((r) => ({ ...r, network_ids: r.network_ids || [] }))));
    }
    setLoading(false);
  }, [networkId]);

  useEffect(() => {
    load();
  }, [load]);

  const scored = rows.filter((r) => r.tier === "scored");
  const totalBids = rows.reduce((s, r) => s + r.bids, 0);
  const lanesCovered = rows.reduce((s, r) => s + r.shared_lanes, 0);

  return {
    rows,
    scored,
    loading,
    error,
    reload: load,
    memberCount: rows.length,
    totalBids,
    lanesCovered,
    hasRanking: scored.length > 0,
  };
}
